import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Check } from "lucide-react";
import { getSignupSource } from "../lib/source";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadCaptureForm() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        ...form,
        sms_opt_in: smsOptIn,
        source: getSignupSource(),
      });
      setDone(true);
      toast.success(res.data.message || "Thanks! Elizabeth will be in touch.");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40";

  return (
    <section id="keep-in-touch" className="py-16 px-4 sm:px-6 border-t border-border" data-testid="lead-capture-section">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <p className="overline mb-3">Stay Connected</p>
          <h2 className="font-cormorant text-3xl sm:text-4xl font-semibold text-foreground mb-3">
            Let's keep in touch
          </h2>
          <p className="font-manrope text-sm text-muted-foreground max-w-md mx-auto">
            Leave your details and Elizabeth will reach out with writing insights,
            offers, and ways she can help your business grow.
          </p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-xl p-8 text-center"
            data-testid="lead-success"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-50 dark:bg-green-900/20 mb-4">
              <Check size={24} className="text-green-600" />
            </div>
            <h3 className="font-cormorant text-2xl font-semibold text-foreground mb-2">You're on the list!</h3>
            <p className="font-manrope text-sm text-muted-foreground">
              Thank you — Elizabeth will be in touch soon.
            </p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-6 sm:p-8" data-testid="lead-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input
                type="text" required placeholder="First name"
                value={form.first_name} onChange={set("first_name")}
                data-testid="lead-first-name-input" className={inputClass}
              />
              <input
                type="text" placeholder="Last name"
                value={form.last_name} onChange={set("last_name")}
                data-testid="lead-last-name-input" className={inputClass}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <input
                type="email" required placeholder="Email address"
                value={form.email} onChange={set("email")}
                data-testid="lead-email-input" className={inputClass}
              />
              <input
                type="tel" placeholder="Phone number"
                value={form.phone} onChange={set("phone")}
                data-testid="lead-phone-input" className={inputClass}
              />
            </div>
            <label className="flex items-start gap-2.5 mb-5 cursor-pointer">
              <input
                type="checkbox"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                data-testid="lead-sms-optin-checkbox"
                className="mt-0.5 h-4 w-4 rounded border-border accent-[#E07A5F] cursor-pointer"
              />
              <span className="font-manrope text-xs text-muted-foreground leading-relaxed">
                I agree to receive text messages.
              </span>
            </label>
            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-brand-accent text-white font-manrope text-sm font-semibold hover:bg-brand-accent-hover disabled:opacity-60 transition-colors"
            >
              <Send size={15} />
              {submitting ? "Sending..." : "Keep me posted"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
